import dynamic from 'next/dynamic';
import type { VimeoPlayerProps } from 'react-player/vimeo';
import type { YouTubePlayerProps } from 'react-player/youtube';

import { getBaseVimeoUrl } from '@/utils/vimeo';

const VimeoPlayer = dynamic(import('react-player/vimeo'), { ssr: false });
const YoutubePlayer = dynamic(import('react-player/youtube'), { ssr: false });

const isVimeoVideo = (
  props: VimeoPlayerProps | YouTubePlayerProps
): props is VimeoPlayerProps => {
  try {
    const { host } = new URL(props.url?.toString() || '');
    const allowedVimeoHosts = [
      'player.vimeo.com',
      'vimeo.com',
      'www.vimeo.com',
    ];

    return allowedVimeoHosts.includes(host);
  } catch {
    return false;
  }
};
const isYoutubeVideo = (
  props: VimeoPlayerProps | YouTubePlayerProps
): props is YouTubePlayerProps => {
  try {
    const { host } = new URL(props.url?.toString() || '');
    const allowedYoutubeHosts = ['youtube.com', 'www.youtube.com', 'youtu.be'];

    return allowedYoutubeHosts.includes(host);
  } catch {
    return false;
  }
};

type VideoPlayerProps =
  | VimeoPlayerProps
  | (YouTubePlayerProps & { autoplay: boolean });

export default function VideoPlayer({
  autoplay,
  ...playerProps
}: VideoPlayerProps) {
  'use memo';
  const autoplayProps = {
    loop: true,
    playsinline: true,
    muted: true,
    playing: true,
  };

  if (isYoutubeVideo(playerProps)) {
    return (
      <YoutubePlayer
        data-chromatic='ignore'
        {...{
          ...(autoplay ? autoplayProps : {}),
          ...playerProps,
        }}
        config={{
          playerVars: {
            // setting rel to 0 makes the video player only show related videos from the same channel.
            rel: 0,
            ...playerProps.config?.playerVars,
          },
          ...playerProps.config,
        }}
      />
    );
  }

  if (isVimeoVideo(playerProps)) {
    const baseUrl = getBaseVimeoUrl(playerProps.url?.toString() || '');

    return (
      <VimeoPlayer
        data-chromatic='ignore'
        {...{
          ...(autoplay ? autoplayProps : {}),
          ...playerProps,
        }}
        url={baseUrl}
      />
    );
  }

  return null;
}
